import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';

import { JwtHelperService } from '@auth0/angular-jwt';
import * as fromRoot from '@dav-store/reducers';
import * as UserActions from '@dav-store/user/user.actions';
import { Store } from '@ngrx/store';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, fromEvent, Observable, Subject, Subscription, timer } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiService } from './api.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  token: string;
  openTimeoutModal: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  timeoutExpiring: Subject<number> = new Subject<number>();

  private sessionTimer: Observable<number>;
  private sessionTime = 5400000;
  private warningTime = 60000;

  // subscriptions
  private tokenTimerSubscription: Subscription;
  private sessionTimerSubscription: Subscription;
  private resetInactivitySubscription: Subscription;
  private appVersionSubscription: Subscription;
  private tokenApiCallSubscription: Subscription;
  private tokenRefreshedAt: Date;

  constructor(
    private apiService: ApiService,
    private jwtHelper: JwtHelperService,
    private router: Router,
    private store: Store<fromRoot.State>,
    private cookieService: CookieService
  ) {}

  ngOnDestroy(): void {
    this.tokenTimerSubscription?.unsubscribe();
    this.sessionTimerSubscription?.unsubscribe();
    this.resetInactivitySubscription?.unsubscribe();
    this.appVersionSubscription?.unsubscribe();
    this.tokenApiCallSubscription?.unsubscribe();
  }

  resetTokenRefreshTimestamp(): void {
    this.tokenRefreshedAt = new Date();
  }

  startInactivityTimer() {
    this.resetInactivitySubscription?.unsubscribe();
    this.resetInactivitySubscription = fromEvent(document, 'click').subscribe(() => this.startSessionTimer());
  }

  startSessionTimer() {
    this.cancelSessionTimer();
    this.sessionTimer = timer(this.sessionTime - this.warningTime, 1000).pipe(
      map((value) => this.warningTime - value * 1000)
    );

    this.sessionTimerSubscription = this.sessionTimer.subscribe((n) => {
      this.timeoutExpiring.next(n);
      if (n === 0) {
        this.cancelAllSubscriptions();
        this.store.dispatch(new UserActions.LogOutAction());
      }
      if (!this.openTimeoutModal.getValue() && n !== 0) {
        this.openTimeoutModal.next(true);
      }
    });
  }

  cancelSessionTimer() {
    this.sessionTimerSubscription?.unsubscribe();
    this.openTimeoutModal.next(false);
  }

  isAuthenticated(): boolean {
    // Check whether the current time is past the
    // access token's expiry time
    const token = sessionStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('userState'));
    if (token && user.isAuthenticated) {
      try {
        const tokenValidityMilis = this._getTokenValidityLength(token);

        return (
          environment.authMock ||
          !this.tokenRefreshedAt ||
          Date.now() < this.tokenRefreshedAt.getTime() + tokenValidityMilis - 120000
        );
      } catch (error) {
        sessionStorage.removeItem('token');

        return false;
      }
    }

    return false;
  }

  scheduleTokenRenewal() {
    // cancel any token renewal subscriptions
    this.cancelTokenRenewal();

    // check authentication
    if (!this.isAuthenticated()) {
      this.cancelAllSubscriptions();
      this.store.dispatch(new UserActions.LogOutAction());

      return;
    }

    const tokenValidityMilis = this._getTokenValidityLength(sessionStorage.getItem('token'));
    // when does it actually call?? is there a buffer before the call to the api?
    const expiresIn$ = timer(Math.max(1, tokenValidityMilis - 120000), 1000);

    // Once the delay time from above is
    // reached, get a new JWT and schedule
    // additional refreshes
    this.tokenTimerSubscription = expiresIn$.subscribe(() => {
      if (!environment.authMock) {
        this.renewToken();
      }
    });
  }

  cancelTokenRenewal() {
    this.tokenTimerSubscription?.unsubscribe();
  }

  cancelAllSubscriptions() {
    this.tokenTimerSubscription?.unsubscribe();
    this.sessionTimerSubscription?.unsubscribe();
    this.openTimeoutModal.next(false);
    this.resetInactivitySubscription?.unsubscribe();
  }

  renewToken() {
    // Dispatch calling refresh token
    this.tokenApiCallSubscription?.unsubscribe();
    const token = sessionStorage.getItem('token');
    if (!token) {
      return;
    }

    this.tokenApiCallSubscription = this.apiService.refreshToken().subscribe((data) => {
      // If successful, dispatch success action with result
      this.resetTokenRefreshTimestamp();
      sessionStorage.setItem('token', data.token);
      this.scheduleTokenRenewal();
    });
  }

  getCookie() {
    return this.cookieService.get('sso-token');
  }

  private _getTokenValidityLength(token: string) {
    if (environment.authMock) {
      return 1000000000000;
    }

    const { exp, iat } = this.jwtHelper.decodeToken(token);

    return (exp - iat) * 1000;
  }
}
